import { NavTab } from '@csp/csp-web-ui';
import { useEffect, useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { CspPath, DEFAULT_AUTHENTICATED_PATH } from '../../../CspPath';
import { useStudyContext } from '../../../common/context/study/useStudyContext';
import { StaticContentService } from '../../../common/service/StaticContentService';
import { StudyContentService } from '../../../common/service/StudyContentService';
import { isProdLikeEnv } from '../../../system/service/CspConfigService';
import { useQueryTotalRpmEventsNotViewed } from '../../patient-list/patient-list-rpm-events/hook/useQueryTotalRpmEventsNotViewed';
import { useVisitRequests } from '../../patient-list/patient-list-visit/hook/useVisitRequests';

type Out = {
  subNavs: NavTab<CspPath>[];
};

export const usePatientSubTabs = (): Out => {
  const { totalRpmEventsNotViewed } = useQueryTotalRpmEventsNotViewed();
  const { hbsEnabled, isLoadingScheduleRequest } = useVisitRequests();
  const { study } = useStudyContext();
  const navigate = useNavigate();

  const navigationTabTexts = StaticContentService.getGenericBlock('webHcpPatientList', 'navigationTab');
  const rpmTexts = study.hasRpmEvents ? StudyContentService.getRpmMonitoring() : undefined;

  const isVisitEnabledOnAnySite = !!study.features?.summary.visits.isEnabledOnAnySite;
  const isAtCloseoutPath = !!useMatch(CspPath.PATIENT_LIST_CLOSEOUT);
  const isAtBookVisitPath = !!useMatch(CspPath.PATIENT_LIST_VISIT);
  const isAtHbsPath = !!useMatch(CspPath.PATIENT_LIST_HBS);
  const isAtRpmEventsPath = !!useMatch(CspPath.PATIENT_LIST_RPM_EVENTS);

  const shouldNavigateToDefaultPath =
    (isAtCloseoutPath && !study.isCloseoutPeriodEnabled) ||
    (!isVisitEnabledOnAnySite && isAtBookVisitPath) ||
    (!hbsEnabled && !isLoadingScheduleRequest && isAtHbsPath) ||
    (!study.hasRpmEvents && isAtRpmEventsPath);

  useEffect(() => {
    if (shouldNavigateToDefaultPath) {
      navigate(DEFAULT_AUTHENTICATED_PATH, { replace: true });
    }
  }, [navigate, shouldNavigateToDefaultPath]);

  const subNavs: NavTab<CspPath>[] = useMemo(
    () =>
      [
        {
          label: navigationTabTexts.textLabel.bookedVisits.label,
          path: CspPath.PATIENT_LIST_VISIT,
          isEnabledFeature: isVisitEnabledOnAnySite,
        },
        {
          label: navigationTabTexts.textLabel.closeoutTracker.label,
          path: CspPath.PATIENT_LIST_CLOSEOUT,
          isEnabledFeature: study.isCloseoutPeriodEnabled,
        },
        {
          label: navigationTabTexts.textLabel.bioSamples.label,
          path: CspPath.PATIENT_LIST_HBS,
          isEnabledFeature: hbsEnabled,
        },
        {
          label: rpmTexts?.rpm_tab,
          path: CspPath.PATIENT_LIST_RPM_EVENTS,
          isEnabledFeature: study.hasRpmEvents,
          badgeContent: totalRpmEventsNotViewed,
        },
        {
          label: 'Debug',
          path: CspPath.PATIENT_LIST_DEBUG,
          isEnabledFeature: !isProdLikeEnv(),
        },
      ].filter(tab => tab.isEnabledFeature),
    [
      hbsEnabled,
      isVisitEnabledOnAnySite,
      navigationTabTexts.textLabel.bioSamples.label,
      navigationTabTexts.textLabel.bookedVisits.label,
      navigationTabTexts.textLabel.closeoutTracker.label,
      rpmTexts?.rpm_tab,
      study.hasRpmEvents,
      study.isCloseoutPeriodEnabled,
      totalRpmEventsNotViewed,
    ],
  );

  return {
    subNavs,
  };
};
